import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash2, EyeOff, Eye, FileText, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { dataApi } from '../services/api';
import { useDevMode } from '../context/DevModeContext';
import { Tooltip } from './ui/tooltip';
import { Button } from './ui/button';
import { Droppable, pointerFirstCollisionDetection } from './ui/sortable';
import { cn } from '../lib/utils';
import {
  DAMAGE_CATEGORY,
  FIXED_CATEGORIES,
  groupPhotos,
  isPdfFile,
  matchesPhotoFilter,
  movePhoto,
  photoCategories,
  photoChanges,
  photoId,
  zonesWithPhotos,
} from '../lib/photoGroups';
import DeleteConfirmDialog from './dialogs/DeleteConfirmDialog';
import PhotoZoneFilter from './PhotoZoneFilter';

/**
 * ImageManager — a quote request's photos, grouped by category.
 *
 * - Photos are grouped Damage / Other (plus any other category present), each
 *   group in imageOrder
 * - Drag a tile to reorder it, or into another group to change its category;
 *   both are saved and synced to everyone on the request (lib/photoGroups)
 * - Filter by category, and by section with the birds-eye car
 * - The + tile opens the staging dialog (owned by the page)
 * - Click the category chip to toggle whether the AI uses a photo
 * - Click a tile to open it in the image viewer, which then steps through the
 *   photos shown here, in the order shown — so a filtered section cycles its
 *   own photos. A PDF opens in its own tab instead.
 */

const CATEGORY_META = {
  damage: { label: 'Damage', title: 'Damage photos', chipClass: 'bg-primary text-primary-foreground', tip: 'Used for AI damage assessment — click to mark as reference only' },
  extra: { label: 'Other', title: 'Other photos', chipClass: 'bg-background/90 text-muted-foreground border', tip: 'Reference only — click to include in AI damage assessment' },
  vehicle: { label: 'Vehicle', chipClass: 'bg-background/90 text-muted-foreground border', tip: 'Vehicle angle photo — click to include in AI damage assessment' },
  registration: { label: 'Rego', chipClass: 'bg-background/90 text-muted-foreground border', tip: 'Registration photo — click to include in AI damage assessment' },
};

const categoryMeta = (category) =>
  CATEGORY_META[category] || { label: category || 'Other', chipClass: 'bg-background/90 text-muted-foreground border', tip: 'Click to include in AI damage assessment' };

const groupTitle = (category) => CATEGORY_META[category]?.title || categoryMeta(category).label;

const GROUP_PREFIX = 'group:';

const photoName = (image) => image.imageName || image.filename || image.name || 'Quote request photo';

// A PDF has no picture to show — its tile is an icon and its file name.
function PdfFace({ image }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 p-3 text-muted-foreground">
      <FileText className="h-8 w-8" />
      <span className="w-full text-center text-[11px] leading-tight line-clamp-2 break-all">{photoName(image)}</span>
    </div>
  );
}

function SortableImageTile({ image, onOpen, onToggleCategory, onDelete }) {
  const id = photoId(image);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const meta = categoryMeta(image.imageCategory);
  const pdf = isPdfFile(image);
  const { active: devModeActive, isPhotoHidden, togglePhoto } = useDevMode();
  const photoHidden = devModeActive && isPhotoHidden(id);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      className={cn(
        'group relative aspect-square rounded-lg overflow-hidden bg-muted cursor-grab active:cursor-grabbing touch-none',
        isDragging && 'opacity-40'
      )}
    >
      {pdf ? (
        <a
          href={dataApi.getImageFileUrl(image.storagePath)}
          target="_blank"
          rel="noopener noreferrer"
          title={`Open ${photoName(image)}`}
          draggable={false}
          className={cn('block w-full h-full hover:text-foreground', photoHidden && 'invisible')}
        >
          <PdfFace image={image} />
        </a>
      ) : (
        <img
          src={image.url}
          alt={photoName(image)}
          className={cn('w-full h-full object-cover cursor-pointer', photoHidden && 'invisible')}
          onClick={() => onOpen(id)}
          draggable={false}
        />
      )}

      {/* Hidden overlay (dev mode) — replaces the image for screenshots */}
      {photoHidden && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-muted text-muted-foreground text-xs select-none pointer-events-none">
          <EyeOff className="h-5 w-5" />
          <span>Photo hidden</span>
        </div>
      )}

      {/* Hide/show toggle — dev mode only, corner button on hover */}
      {devModeActive && (
        <button
          type="button"
          title={photoHidden ? 'Show photo' : 'Hide photo for screenshots'}
          onClick={(e) => { e.stopPropagation(); togglePhoto(id); }}
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            'absolute bottom-1.5 right-1.5 h-7 w-7 rounded-md bg-background/80 text-muted-foreground hover:text-foreground hover:bg-background flex items-center justify-center transition-opacity z-10',
            photoHidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
          )}
        >
          {photoHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
      )}

      {/* Category chip — toggles AI inclusion. A PDF is never assessed, so it has none. */}
      {!pdf && (
        <Tooltip content={meta.tip}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleCategory(image); }}
            onPointerDown={(e) => e.stopPropagation()}
            className={cn(
              'absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[11px] font-medium leading-4 shadow-sm',
              meta.chipClass
            )}
          >
            {meta.label}
          </button>
        </Tooltip>
      )}

      {/* Delete */}
      <button
        type="button"
        title="Delete photo"
        onClick={(e) => { e.stopPropagation(); onDelete(image); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute top-1.5 right-1.5 h-7 w-7 rounded-md bg-background/80 text-muted-foreground hover:text-destructive hover:bg-background flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

const ImageManager = ({
  images = [],
  onImageRemoved,
  onImageUpdated,
  onImagesReordered,
  onImageClick,
  onRequestUpload,
}) => {
  const [activeId, setActiveId] = useState(null);
  // While a photo is being dragged: each group's visible ids, rearranged live as
  // the photo crosses into another group. null when nothing is being dragged.
  const [dragLists, setDragLists] = useState(null);
  // The photo waiting on its delete confirmation.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [zoneFilter, setZoneFilter] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const categories = useMemo(() => photoCategories(images), [images]);
  const zones = useMemo(() => zonesWithPhotos(images), [images]);
  // A filter can outlive what it pointed at — the last photo of a category or
  // section moved elsewhere — and then falls back to showing everything.
  const activeCategory = categories.includes(categoryFilter) ? categoryFilter : null;
  const activeZone = zones.has(zoneFilter) ? zoneFilter : null;

  // The groups on screen. Damage and Other always show (they are where uploads
  // land and where a photo can be dragged to) unless a section filter is
  // narrowing the view; any other category shows only while it has photos.
  const groups = useMemo(() => groupPhotos(images, categories)
    .filter((group) => !activeCategory || group.category === activeCategory)
    .map((group) => ({
      ...group,
      images: group.images.filter((image) => matchesPhotoFilter(image, { zone: activeZone })),
    }))
    .filter((group) => group.images.length > 0 || (FIXED_CATEGORIES.includes(group.category) && !activeZone)),
  [images, categories, activeCategory, activeZone]);

  const byId = useMemo(() => new Map(images.map((image) => [photoId(image), image])), [images]);
  const lists = dragLists || Object.fromEntries(groups.map((group) => [group.category, group.images.map(photoId)]));
  // What the viewer steps through: the photos shown, in the order shown.
  const viewerImages = groups.flatMap((group) => group.images).filter((image) => !isPdfFile(image));
  const activeImage = activeId ? byId.get(activeId) : null;

  const groupOf = (id, from) => {
    if (typeof id === 'string' && id.startsWith(GROUP_PREFIX)) return id.slice(GROUP_PREFIX.length);
    return Object.keys(from).find((category) => from[category].includes(id)) || null;
  };

  const resetDrag = () => {
    setActiveId(null);
    setDragLists(null);
  };

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    setDragLists(Object.fromEntries(groups.map((group) => [group.category, group.images.map(photoId)])));
  };

  // Live relocation across groups while dragging (as in the Add photos dialog)
  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    setDragLists((prev) => {
      if (!prev) return prev;
      const from = groupOf(active.id, prev);
      const to = groupOf(over.id, prev);
      if (!from || !to || from === to || !prev[to]) return prev;
      // A PDF is never a damage photo: the AI cannot look at it.
      if (to === DAMAGE_CATEGORY && isPdfFile(byId.get(active.id))) return prev;

      const toList = [...prev[to]];
      const overIndex = toList.indexOf(over.id);
      toList.splice(overIndex >= 0 ? overIndex : toList.length, 0, active.id);
      return { ...prev, [from]: prev[from].filter((id) => id !== active.id), [to]: toList };
    });
  };

  const handleDragEnd = async ({ active, over }) => {
    const finalLists = dragLists;
    resetDrag();
    if (!over || !finalLists) return;

    const category = groupOf(active.id, finalLists);
    if (!category) return;
    let targetIds = finalLists[category];
    const fromIndex = targetIds.indexOf(active.id);
    const toIndex = targetIds.indexOf(over.id);
    if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
      targetIds = arrayMove(targetIds, fromIndex, toIndex);
    }

    const moved = movePhoto(images, active.id, category, targetIds);
    const changes = photoChanges(images, moved);
    if (changes.length === 0) return;

    onImagesReordered(moved);
    try {
      await dataApi.saveQuoteRequestImagePositions(changes);
    } catch (err) {
      console.error('Failed to persist photo order:', err);
      toast.error('Failed to save the new photo order');
    }
  };

  const handleToggleCategory = async (image) => {
    const imageId = photoId(image);
    const nextCategory = image.imageCategory === 'damage' ? 'extra' : 'damage';
    onImageUpdated(imageId, { imageCategory: nextCategory });
    try {
      await dataApi.updateQuoteRequestImage(imageId, { imageCategory: nextCategory });
    } catch (err) {
      console.error('Failed to update image category:', err);
      onImageUpdated(imageId, { imageCategory: image.imageCategory });
      toast.error('Failed to update photo category');
    }
  };

  const handleDelete = (image) => setPendingDelete(image);

  const confirmDelete = async () => {
    const image = pendingDelete;
    const imageId = photoId(image);
    try {
      await dataApi.deleteQuoteRequestImage(image);
      onImageRemoved(imageId);
    } catch (err) {
      console.error('Failed to delete image:', err);
      toast.error(err?.message || 'Failed to delete photo');
    }
  };

  // Hiding the filter pane clears its filter, so the grid is never left
  // filtered with no visible control to clear it.
  const toggleFilter = () => {
    if (filterOpen) setZoneFilter(null);
    setFilterOpen(!filterOpen);
  };

  const categoryOptions = [
    { value: null, label: 'All' },
    ...categories.map((category) => ({ value: category, label: categoryMeta(category).label })),
  ];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerFirstCollisionDetection}
      onDragStart={handleDragStart}
      onDragCancel={resetDrag}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-4">
        {images.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={filterOpen ? 'default' : 'outline'}
              size="sm"
              aria-pressed={filterOpen}
              onClick={toggleFilter}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
            <div role="group" aria-label="Photo category" className="inline-flex rounded-md border border-input bg-input p-0.5">
              {categoryOptions.map((option) => (
                <button
                  key={option.value ?? 'all'}
                  type="button"
                  aria-pressed={activeCategory === option.value}
                  onClick={() => setCategoryFilter(option.value)}
                  className={cn(
                    'h-8 px-3 rounded text-xs font-medium transition-colors',
                    activeCategory === option.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-6 items-start">
          {filterOpen && (
            <div className="w-36 flex-shrink-0 sticky top-4 self-start">
              <PhotoZoneFilter zonesWithPhotos={zones} value={activeZone} onChange={setZoneFilter} />
            </div>
          )}

          <div className="flex-1 min-w-0 space-y-5">
            {groups.map((group) => {
              const tiles = (lists[group.category] || []).map((id) => byId.get(id)).filter(Boolean);
              return (
                <section key={group.category} aria-label={groupTitle(group.category)} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{groupTitle(group.category)}</p>
                    {tiles.length > 0 && <span className="text-xs text-muted-foreground">{tiles.length}</span>}
                  </div>
                  <Droppable id={`${GROUP_PREFIX}${group.category}`} className="rounded-lg">
                    <SortableContext items={tiles.map(photoId)} strategy={rectSortingStrategy}>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
                        {tiles.map((image) => (
                          <SortableImageTile
                            key={photoId(image)}
                            image={image}
                            onOpen={(id) => onImageClick(id, viewerImages)}
                            onToggleCategory={handleToggleCategory}
                            onDelete={handleDelete}
                          />
                        ))}

                        {/* Add photos — opens the staging dialog */}
                        {FIXED_CATEGORIES.includes(group.category) && (
                          <button
                            type="button"
                            aria-label="Add photos"
                            title="Add photos"
                            onClick={onRequestUpload}
                            className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 text-muted-foreground hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
                          >
                            <Plus className="h-6 w-6" />
                          </button>
                        )}
                      </div>
                    </SortableContext>
                  </Droppable>
                </section>
              );
            })}

            {groups.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">No photos match this filter</p>
            )}
          </div>
        </div>
      </div>

      <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
        {activeImage ? (
          <div className="aspect-square rounded-lg overflow-hidden bg-muted shadow-lg ring-1 ring-black/10">
            {isPdfFile(activeImage)
              ? <PdfFace image={activeImage} />
              : <img src={activeImage.url} alt="" className="w-full h-full object-cover" draggable={false} />}
          </div>
        ) : null}
      </DragOverlay>

      <DeleteConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        onConfirm={confirmDelete}
      />
    </DndContext>
  );
};

export default ImageManager;
